'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {apiPost} from '@/lib/api'
import {
    getAnyString,
    getData,
    getMessage,
    getQrPollingMessage,
    getQrPollingState,
    parseAccount,
    saveStoredAccount,
} from '@/lib/utils'
import type {Account, ApiResponse} from '@/types'

type RefreshQrInput = {
    protocol?: string
    proxyUrl?: string
    wxid?: string
}

type UseLoginOptions = {
    onProfileRefresh: (account: Account) => Promise<void> | void
}

type UseLoginResult = {
    qrImage: string
    qrUuid: string
    qrRemainingSeconds: number
    loginStatus: string
    secureVerifyUrl: string
    loginLoading: boolean
    captcha: LoginImageCaptcha | null
    captchaSubmitting: boolean
    loginSuccessVersion: number
    refreshQr: (input?: RefreshQrInput) => Promise<void>
}

const INITIAL_POLL_MS = 2000
const FAST_POLL_MS = 1500
const BACKOFF_FACTOR = 1.5
const BACKOFF_MAX_MS = 15_000
const IDLE_TIMEOUT_MS = 3 * 60_000

const LOGIN_QR_ENDPOINT = '/login/qrcode'

export type LoginImageCaptcha = {
    type: 'wtlogin_image_captcha'
    uuid: string
    imgSid: string
    imageData: string
}

function needsImageCaptcha(response: ApiResponse): boolean {
    return response.Code === -6 || response.code === -6
}

function needsSecureVerify(response: ApiResponse): boolean {
    const data = getObjectData(response)
    return (response.Code === -3 || response.code === -3) && Boolean(data.ticketUrl || data.TicketUrl)
}

function isTerminalLoginResponse(response: ApiResponse): boolean {
    const data = getData(response)
    // Code:200 + loginState:"online" 是登录成功，不算失败
    const loginState = getAnyString(data, ['loginState', 'LoginState'])
    const code = response.Code ?? response.code
    const progress = getAnyString(data, ['loginProgress'])
    if (progress === 'failed') return true
    if (code === 200 && loginState === 'online') return false

    const statusText = getAnyString(data, ['status', 'Status'])
    const status = statusText ? Number(statusText) : null
    if (status !== null && status >= 2) return true
    if (typeof code === 'number' && code < 0 && code !== -3 && code !== -6) return true
    // Redirect/reconnect can temporarily return "link missing" or "offline".
    // The server's explicit pending state takes precedence over message text;
    // the existing QR deadline still bounds the authentication wait.
    if (code === 200 && (progress === 'confirming' || progress === 'switching')) return false
    return /过期|失效|超时|取消|失败|不存在/.test(getMessage(response))
}

function parseImageCaptcha(response: ApiResponse): LoginImageCaptcha | null {
    const data = getData(response)
    if (!needsImageCaptcha(response) || !data || typeof data !== 'object') return null
    const object = data as Record<string, unknown>
    if (object.type !== 'wtlogin_image_captcha') return null
    const imageData = String(object.imageData || '')
    if (!imageData) return null
    return {
        type: 'wtlogin_image_captcha',
        uuid: String(object.uuid || ''),
        imgSid: String(object.imgSid || ''),
        imageData,
    }
}

function getObjectData(response: ApiResponse): Record<string, unknown> {
    const data = getData(response)
    return data && typeof data === 'object' ? data as Record<string, unknown> : {}
}

async function cancelLoginSession(sessionId: string) {
    if (!sessionId) return
    await apiPost('/login/cancel?sessionId=' + encodeURIComponent(sessionId), undefined, {silent: true})
        .catch(() => undefined)
}

export function useLogin(
    account: Account | null,
    setAccount: (next: Account | null) => void,
    options: UseLoginOptions,
): UseLoginResult {
    const [qrImage, setQrImage] = useState('')
    const [qrUuid, setQrUuid] = useState('')
    const [qrExpiresAt, setQrExpiresAt] = useState(0)
    const [qrRemainingSeconds, setQrRemainingSeconds] = useState(0)
    const [loginStatus, setLoginStatus] = useState('')
    const [secureVerifyUrl, setSecureVerifyUrl] = useState('')
    const [loginLoading, setLoginLoading] = useState(false)
    const [captcha, setCaptcha] = useState<LoginImageCaptcha | null>(null)
    const [captchaSubmitting] = useState(false)
    const [loginSuccessVersion, setLoginSuccessVersion] = useState(0)
    const qrVersionRef = useRef('')
    const attemptRef = useRef(0)
    const firstStageRef = useRef(false)
    const switchingRef = useRef(false)

    const {onProfileRefresh} = options

    const completeLogin = useCallback(async (response: ApiResponse) => {
        const parsed = parseAccount(getData(response))
        if (!parsed) return false

        saveStoredAccount(parsed)
        setAccount(parsed)
        setCaptcha(null)
        setSecureVerifyUrl('')
        setQrUuid('')
        setQrImage('')
        setQrExpiresAt(0)
        setQrRemainingSeconds(0)
        setLoginStatus('登录成功')
        setLoginLoading(false)
        // Save as current instance on server
        apiPost('/current-instance', {wxid: parsed.wxid}, {silent: true}).catch(() => {})
        void onProfileRefresh(parsed)
        return true
    }, [onProfileRefresh, setAccount])

    const refreshQr = useCallback(async (input: RefreshQrInput = {}) => {
        const attempt = ++attemptRef.current
        qrVersionRef.current = ''
        firstStageRef.current = input.protocol === 'mac' || input.protocol === 'windows'
        switchingRef.current = false
        setLoginLoading(true)
        setLoginStatus('正在获取二维码...')
        setSecureVerifyUrl('')
        setCaptcha(null)
        setQrImage('')
        setQrUuid('')
        setQrExpiresAt(0)
        setQrRemainingSeconds(0)
        try {
            if (qrUuid) await cancelLoginSession(qrUuid)
            if (attempt !== attemptRef.current) return
            const response = await apiPost<{
                success: boolean
                sessionId: string
                qrCodeUrl: string
            }>(LOGIN_QR_ENDPOINT, {
                protocol: input.protocol || 'ipad',
                proxyUrl: input.proxyUrl?.trim() || '',
                wxid: input.wxid || '',
            }, {silent: true})

            if (attempt !== attemptRef.current) {
                await cancelLoginSession(response.sessionId)
                return
            }

            if (!response.success || !response.sessionId || !response.qrCodeUrl) {
                setLoginStatus('获取二维码失败')
                return
            }

            setQrImage(response.qrCodeUrl)
            setQrUuid(response.sessionId)
            setQrExpiresAt(Date.now() + IDLE_TIMEOUT_MS)
            setQrRemainingSeconds(Math.ceil(IDLE_TIMEOUT_MS / 1000))
            setLoginStatus(input.protocol === 'mac' || input.protocol === 'windows'
                ? '第 1/2 步：待扫码'
                : '待扫码')
        } catch (error) {
            if (attempt !== attemptRef.current) return
            setLoginStatus(error instanceof Error ? error.message : '获取二维码失败')
            setSecureVerifyUrl('')
        } finally {
            if (attempt === attemptRef.current) setLoginLoading(false)
        }
    }, [qrUuid])

    useEffect(() => {
        if (!qrUuid || !qrExpiresAt) return

        let cancelled = false
        const expire = async () => {
            if (cancelled) return
            await cancelLoginSession(qrUuid)
            if (cancelled) return
            setLoginStatus('二维码已过期，请重新获取')
            setLoginLoading(false)
            setQrUuid('')
            setQrImage('')
            setQrExpiresAt(0)
            setQrRemainingSeconds(0)
        }

        const updateRemaining = () => {
            const next = Math.max(0, Math.ceil((qrExpiresAt - Date.now()) / 1000))
            setQrRemainingSeconds(next)
            if (next <= 0) {
                void expire()
            }
        }

        updateRemaining()
        const timer = window.setInterval(updateRemaining, 1000)
        return () => {
            cancelled = true
            window.clearInterval(timer)
        }
    }, [qrExpiresAt, qrUuid])

    useEffect(() => {
        if (!qrUuid || !qrExpiresAt) return

        let cancelled = false
        let timer: ReturnType<typeof setTimeout> | null = null
        let interval = INITIAL_POLL_MS
        const attempt = attemptRef.current

        const schedule = (delay: number) => {
            if (cancelled || attempt !== attemptRef.current) return
            if (timer) clearTimeout(timer)
            timer = setTimeout(tick, delay)
        }

        const tick = async () => {
            if (cancelled) return

            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return
            }

            if (Date.now() >= qrExpiresAt) {
                await cancelLoginSession(qrUuid)
                if (!cancelled) {
                    setLoginStatus('二维码已过期，请重新获取')
                    setLoginLoading(false)
                    setQrUuid('')
                    setQrImage('')
                    setQrExpiresAt(0)
                    setQrRemainingSeconds(0)
                }
                return
            }

            let response: ApiResponse
            try {
                response = await apiPost<ApiResponse>(
                    '/login/check?sessionId=' + encodeURIComponent(qrUuid),
                    undefined,
                    {silent: true},
                )
            } catch (error) {
                if (cancelled || attempt !== attemptRef.current) return
                const message = error instanceof Error ? error.message : ''
                if (message.includes('登录会话已过期')) {
                    setLoginLoading(false)
                    setLoginStatus('登录会话已过期，请重新获取二维码')
                    setQrUuid('')
                    setQrImage('')
                    setQrExpiresAt(0)
                    setQrRemainingSeconds(0)
                    return
                }
                setLoginStatus(message || '登录状态查询暂时失败，正在重试...')
                setLoginLoading(false)
                interval = Math.min(interval * BACKOFF_FACTOR, BACKOFF_MAX_MS)
                schedule(interval)
                return
            }

            if (cancelled || attempt !== attemptRef.current) return

            const pollData = getObjectData(response)
            const nextQrVersion = String(pollData.qrVersion || '')
            if (pollData.loginStage === 'ipad' && nextQrVersion && pollData.qrCodeUrl
                && qrVersionRef.current !== nextQrVersion) {
                qrVersionRef.current = nextQrVersion
                setLoginLoading(false)
                setQrImage(String(pollData.qrCodeUrl))
                setQrExpiresAt(Date.now() + IDLE_TIMEOUT_MS)
                setQrRemainingSeconds(Math.ceil(IDLE_TIMEOUT_MS / 1000))
                setCaptcha(null)
                setSecureVerifyUrl('')
                setLoginStatus(pollData.transitionReason === 'client_version_too_low'
                    ? '第 2/2 步：待扫码（首次认证版本过低，请扫描新的 iPad 二维码）'
                    : '第 2/2 步：待扫码（首次登录成功，请扫描新的 iPad 二维码）')
                // The updated expiry restarts polling without cancelling the shared server session.
                schedule(FAST_POLL_MS)
                return
            }

            if (needsImageCaptcha(response)) {
                const nextCaptcha = parseImageCaptcha(response)
                setCaptcha(nextCaptcha)
                setSecureVerifyUrl('')
                setLoginStatus(nextCaptcha ? '请输入图片验证码' : getQrPollingMessage(response))
                return
            }

            if (needsSecureVerify(response)) {
                const data = getObjectData(response)
                const ticketUrl = String(data.ticketUrl || data.TicketUrl || '')
                setSecureVerifyUrl(ticketUrl)
                setLoginStatus(getMessage(response) || '需要提交验证码后登录')
                interval = FAST_POLL_MS
                schedule(interval)
                return
            }

            setSecureVerifyUrl('')
            const state = getQrPollingState(response)
            const prefix = qrVersionRef.current ? '第 2/2 步：' : firstStageRef.current ? '第 1/2 步：' : ''
            if (state === 'switching') {
                setLoginStatus(prefix + getQrPollingMessage(response))
                setLoginLoading(true)
                if (!switchingRef.current) {
                    switchingRef.current = true
                    setQrImage('')
                    setQrExpiresAt(Date.now() + IDLE_TIMEOUT_MS)
                }
                schedule(FAST_POLL_MS)
                return
            }
            if (state === 'done') {
                if (await completeLogin(response)) {
                    setLoginSuccessVersion((version) => version + 1)
                } else {
                    setLoginStatus(prefix + '登录成功，正在获取账号信息')
                    schedule(FAST_POLL_MS)
                }
                return
            }

            setLoginStatus(prefix + getQrPollingMessage(response))

            if (isTerminalLoginResponse(response)) {
                setLoginLoading(false)
                setQrUuid('')
                setQrImage('')
                setQrExpiresAt(0)
                setQrRemainingSeconds(0)
                return
            }

            interval = state === 'scanned' || state === 'confirming'
                ? FAST_POLL_MS
                : INITIAL_POLL_MS
            schedule(interval)
        }

        const onVisibilityChange = () => {
            if (cancelled) return
            if (document.visibilityState === 'visible') {
                interval = INITIAL_POLL_MS
                schedule(0)
            }
        }

        schedule(0)

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange)
        }

        return () => {
            cancelled = true
            if (timer) {
                clearTimeout(timer)
                timer = null
            }
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange)
            }
        }
    }, [account, completeLogin, qrExpiresAt, qrUuid])

    return {
        qrImage,
        qrUuid,
        qrRemainingSeconds,
        loginStatus,
        secureVerifyUrl,
        loginLoading,
        captcha,
        captchaSubmitting,
        loginSuccessVersion,
        refreshQr,
    }
}
